import { useState, useCallback, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Stack,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Alert,
    IconButton,
    Typography as MuiTypography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { queryStorageLocations, queryItemTypesWithLocations, StorageLocation } from '../schema';
import StorageLocationForm, { StorageLocationFormValues } from './StorageLocationForm';
import {
    createStorageLocation,
    updateStorageLocation,
    deleteStorageLocation,
    canDeleteStorageLocation,
} from '../utils/storageLocationUtils';
import LoadingIndicator from './LoadingIndicator';
import { usePermissions } from '../hooks/usePermissions';
import PermissionDenied from './PermissionDenied';

export default function StorageLocationManager() {
    const { canViewInventory, isLoading: permissionsLoading } = usePermissions();
    const { data: locationsData, isLoading, error } = queryStorageLocations();
    const { data: itemTypesData } = queryItemTypesWithLocations();

    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState<StorageLocation | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState<StorageLocation | undefined>(undefined);
    const [err, setErr] = useState<string | null>(null);

    const locations = useMemo(() => {
        const list = locationsData?.storageLocations ?? [];
        return [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [locationsData]);

    const itemLocationsWithStorage = useMemo(() => {
        const out: Array<{ storageLocation?: { id: string } | null }> = [];
        for (const it of itemTypesData?.itemTypes ?? []) {
            const locs = (it as { itemLocations?: Array<{ storageLocation?: { id: string } | null }> }).itemLocations ?? [];
            for (const loc of locs) {
                if (loc.storageLocation) out.push(loc);
            }
        }
        return out;
    }, [itemTypesData]);

    const handleCreate = () => {
        setEditingLocation(undefined);
        setFormDialogOpen(true);
        setErr(null);
    };

    const handleEdit = (loc: StorageLocation) => {
        setEditingLocation(loc);
        setFormDialogOpen(true);
        setErr(null);
    };

    const handleDeleteClick = (loc: StorageLocation) => {
        setLocationToDelete(loc);
        setDeleteDialogOpen(true);
        setErr(null);
    };

    const handleFormSubmit = useCallback(
        async (values: StorageLocationFormValues) => {
            try {
                setErr(null);
                if (editingLocation) {
                    await updateStorageLocation(editingLocation.id, { name: values.name.trim() });
                } else {
                    const maxOrder = Math.max(0, ...locations.map(l => l.order ?? 0));
                    await createStorageLocation({ name: values.name.trim(), order: maxOrder + 1 });
                }
                setFormDialogOpen(false);
                setEditingLocation(undefined);
            } catch (e: unknown) {
                setErr(e instanceof Error ? e.message : 'Failed to save');
            }
        },
        [editingLocation, locations]
    );

    const handleDeleteConfirm = useCallback(async () => {
        if (!locationToDelete) return;
        const check = canDeleteStorageLocation(locationToDelete.id, itemLocationsWithStorage);
        if (!check.canDelete) {
            setErr(check.reason ?? 'Cannot delete');
            return;
        }
        try {
            setErr(null);
            await deleteStorageLocation(locationToDelete);
            setDeleteDialogOpen(false);
            setLocationToDelete(undefined);
        } catch (e: unknown) {
            setErr(e instanceof Error ? e.message : 'Failed to delete');
        }
    }, [locationToDelete, itemLocationsWithStorage]);

    if (!permissionsLoading && !canViewInventory) {
        return <PermissionDenied requiredPermission="View Inventory" />;
    }

    if (isLoading) {
        return <LoadingIndicator message="Loading storage locations..." />;
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error">Error loading storage locations: {error.message}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 3, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
            <Stack spacing={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4">Storage Locations</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                        Add location
                    </Button>
                </Box>

                {err && (
                    <Alert severity="error" onClose={() => setErr(null)}>
                        {err}
                    </Alert>
                )}

                <Paper sx={{ p: 2 }}>
                    {locations.length === 0 ? (
                        <MuiTypography color="text.secondary" align="center" sx={{ py: 4 }}>
                            No storage locations yet. Add one (e.g. HQ) to track inventory by location.
                        </MuiTypography>
                    ) : (
                        <Stack spacing={1}>
                            {locations.map(loc => {
                                const delCheck = canDeleteStorageLocation(loc.id, itemLocationsWithStorage);
                                return (
                                    <Box
                                        key={loc.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <MuiTypography variant="body1" fontWeight={500} sx={{ flex: 1 }}>
                                            {loc.name}
                                        </MuiTypography>
                                        <IconButton size="small" onClick={() => handleEdit(loc)} aria-label={`Edit ${loc.name}`}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteClick(loc)}
                                            disabled={!delCheck.canDelete}
                                            aria-label={delCheck.canDelete ? `Delete ${loc.name}` : delCheck.reason}
                                            title={delCheck.reason}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Paper>

                <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>{editingLocation ? 'Edit storage location' : 'Add storage location'}</DialogTitle>
                    <DialogContent>
                        <StorageLocationForm
                            storageLocation={editingLocation}
                            onSubmit={handleFormSubmit}
                            onCancel={() => setFormDialogOpen(false)}
                        />
                    </DialogContent>
                </Dialog>

                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Delete storage location</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <MuiTypography>
                                Are you sure you want to delete &quot;{locationToDelete?.name}&quot;? This cannot be undone.
                            </MuiTypography>
                            {err && <Alert severity="error">{err}</Alert>}
                            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                                <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                                <Button
                                    onClick={handleDeleteConfirm}
                                    color="error"
                                    variant="contained"
                                    disabled={
                                        locationToDelete &&
                                        !canDeleteStorageLocation(locationToDelete.id, itemLocationsWithStorage).canDelete
                                    }
                                >
                                    Delete
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogContent>
                </Dialog>
            </Stack>
        </Container>
    );
}
